import { Input } from "@moe/oss/ui/input";
import { Textarea } from "@moe/oss/ui/textarea";
import { cn } from "@moe/oss/utils/utils";
import { useFindTagAtCaret } from "@web/components/tag/use-find-tag-at-caret";
import { useTagSelect } from "@web/components/tag/use-tag-select";
import { useTagSuggest } from "@web/components/tag/use-tag-suggest";
import { ChangeEvent, forwardRef, KeyboardEvent, useCallback, useRef } from "react";
import { useTagBox } from "./TagBox";

interface TagInputProps extends React.InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement> {
  multiline?: boolean;
  rows?: number;
  placeholder?: string;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  className?: string;
}

/**
 * Manages tag string input, support multiline input (textarea).
 */
export const TagInput = forwardRef<HTMLInputElement | HTMLTextAreaElement, TagInputProps>(
  ({ multiline = false, rows = 3, placeholder = "", onKeyDown, className, ...rest }, forwardedRef) => {
    const localInputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
    const { setOpen, value, setSuggestions, suggestions, setIDX, mode, onChange, inputRef } = useTagBox();
    const suggest = useTagSuggest();
    const select = useTagSelect();
    const findTagAtCaret = useFindTagAtCaret();

    const callbackRef = useCallback(
      (node: HTMLInputElement | HTMLTextAreaElement | null) => {
        localInputRef.current = node;
        // Update the global inputRef on mount
        inputRef.current = node;
        if (typeof forwardedRef === "function") {
          forwardedRef(node);
        } else if (forwardedRef) {
          forwardedRef.current = node;
        }
      },
      [forwardedRef, inputRef]
    );

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (e.key !== "Enter") onKeyDown?.(e);
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setIDX((prev) => (prev < suggestions.length - 1 ? prev + 1 : prev));
          return;

        case "ArrowUp":
          e.preventDefault();
          setIDX((prev) => (prev > 0 ? prev - 1 : prev));
          return;

        case "Enter": {
          if (suggestions.length === 0) {
            onKeyDown?.(e);
            return;
          }
          // We use the enter key to auto-complete the tag, so we need to prevent the default behavior of newline on enter.
          if (multiline) e.preventDefault();
          const didSelect = select();

          // If a tag was selected we don't want to bubble the on keydown event up
          if (!didSelect) onKeyDown?.(e);

          // Reset to the default set of suggestions
          suggest("");
          // In text mode, we close the tag list immediately after selecting a tag.
          if (mode === "text") setOpen(false);
          return;
        }
        case "Escape":
          setSuggestions([]);
          return;
        case "Tab":
          e.preventDefault();
          select();
          // Reset to the default set of suggestions
          suggest("");
          return;
      }
    };

    const handleInput = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const newValue = e.target.value;
      onChange(newValue);
      let query: string = newValue;
      // In text mode, we suggest tags based on the current tag at the caret's position.
      if (mode === "text") {
        const tagInfo = findTagAtCaret();
        if (tagInfo) {
          query = tagInfo.text;
        }
        // Clear suggestions if there are no tags at the caret.
        else {
          setSuggestions([]);
          return;
        }
      }
      suggest(query);
    };

    // In combobox mode, we want to have suggestions available right away on focus.
    const handleFocus = () => {
      if (mode === "combo") suggest("");
    };

    const InputComponent = multiline ? Textarea : Input;
    return (
      <InputComponent
        ref={callbackRef}
        className={cn(
          "text-tx-primary bg-input-primary w-full grow placeholder:select-none focus:outline-none",
          className
        )}
        placeholder={placeholder}
        value={value}
        onChange={handleInput}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        rows={multiline ? rows : undefined}
        {...rest}
      />
    );
  }
);
